import { useState, useEffect, useCallback, useMemo } from "react"
import { useStore } from "../../../context/auth/auth"
import { getTrialFormId } from "../../functions/getTrialForm"
import {
  productsEndpoint,
  checkoutBaseUrl,
} from "../../config/ecomConfig"
import { refreshAuthToken } from "../../functions/refreshAuthToken"
import { legalEntityData, defaultCurrencies } from "./legalEntities"
import { 
  getLocation,
  createAnonynousCart,
  getAccounts,
  provisionAccount,
  getAnonymousCartData,
  getRegisteredCartData,
  assignAnonymousCart,
 } from "./api"

const isTokenValid = timeStamp => getTrialFormId(Number(timeStamp))

const getTIDAuth = () => {
  const TIDAuth = JSON?.parse(localStorage.getItem("user-trial-data"))

  // MAKE SURE THAT THE USER IS AUTHENTICATED AND THAT THE TOKEN IS STILL VALID
  if (TIDAuth && isTokenValid(TIDAuth?.time)) {
    return TIDAuth
  }

  return null
}

const formatLineItems = (newItems, oldItems) => {
  const lineItems = newItems?.map((newItem, i) => {
    const oldItem = oldItems?.find(d => d?.sku === newItem?.sku)
    return {
      ...oldItem, // ADD imageURL WHICH IS ONLY RETURNED IN INITIAL CART DATA
      ...newItem,
      max: 40,
      isLoading: false,
    }
  })
  return lineItems
}

const signInToCheckout = paramObj => {
  const checkout = sessionStorage.getItem("checkout")
  if (checkout) {
    sessionStorage.removeItem("checkout")
    let params = new URLSearchParams()
    Object.keys(paramObj).forEach(key => {
      params.set(key, paramObj[key])
    })
    window.location.replace(`${checkoutBaseUrl}/checkout?${params.toString()}`)
  }

  return !!checkout
}

const useFlyoutCart = ({
  isAuthenticated,
  disableCartFlyout,
  rawFlyoutCartData,
  queryString,
  path,
  products,
  isMigratingAccount,
  setMigratingAccount,
  isServerError,
  setServerError,
  setSubscriptionError,
  client_id,
  client_secret,
  redirect_uri,
  code_verifier,
  setProjectSightProductRemoved,
  setCartUpdatedByAPI,
  setMultiErrorMessage,
}) => {
  const [state, dispatch] = useStore()
  const [url, setUrl] = useState("")
  const [referrerUrl, setReferrerUrl] = useState("")
  const [isCartFlyoutOpen, setIsCartFlyoutOpen] = useState(false)
  const [account, setAccount] = useState(null)
  const [userKey, setUserKey] = useState(null)
  const [locationInfo, setLocationInfo] = useState(null)
  const [currencies, setCurrencies] = useState([])
  const [tokenRefreshing, setTokenRefreshing] = useState(false)
  const [countryCode, setCountryCode] = useState(null)
  const [isProjectSight, setIsProjectSight] = useState(false)

  useEffect(() => {
    setUrl(window.location.href)
    setReferrerUrl(encodeURIComponent(window.location.href))
  }, [])

  useEffect(() => {
    const countryCode = localStorage?.getItem("country")
      ? localStorage?.getItem("country")
      : state?.activeAccount?.address?.country
      ? state?.activeAccount?.address?.country
      : state?.location?.countryCode
    if (countryCode) {
      setCountryCode(countryCode)
    }
  }, [state?.location?.countryCode, state?.activeAccount?.address?.country])

  useEffect(() => {
    state?.cart?.lineItems?.forEach(item => {
      if (item?.sku.indexOf("SB-PS") === 0) {
        setIsProjectSight(true)
      }
    })
  }, [state?.cart?.lineItems])

  // if page has been idling for over an hour and a user opens the flyout and their token is expired, we want to ensure we fresh their token immediately
  useEffect(() => {
    if (typeof window !== "undefined" && !tokenRefreshing) {
      const activeToken = getTIDAuth()
      if (
        !activeToken &&
        JSON?.parse(localStorage.getItem("user-trial-data"))
      ) {
        setTokenRefreshing(true)
        // setTokenNeedsRefresh(true)
        refreshAuthToken(
          client_id,
          client_secret,
          redirect_uri,
          code_verifier,
          path,
          process.env.GATSBY_ENV === "staging"
            ? "https://stage.id.trimblecloud.com/oauth/token"
            : "https://id.trimble.com/oauth/token",
          setUserKey,
          setTokenRefreshing
        )
      }
    }
  }, [isCartFlyoutOpen, state?.cart])

  const handleServerError = useCallback(() => {
    setServerError(true)
  }, [setServerError])

  const updateCart = (data, openFlyout = true) => {
    if (data?.lineItems?.length > 0) {
      const lineItems = formatLineItems(data?.lineItems, state?.cart?.lineItems)
      const payload = {
        ...state?.cart,
        ...data,
        lineItems,
        openFlyout,
      }
      dispatch({
        type: "UPDATE_CART",
        payload: payload,
      })
    } else {
      dispatch({
        type: "UPDATE_CART",
        payload: data,
      })
    }
  }

  const handleCurrencyChange = async currency => {
    dispatch({
      type: "SET_CURRENCY",
      payload: currency,
    })
  }

  useEffect(() => {
    const setCurrency = code => {
      dispatch({
        type: "SET_CURRENCY",
        payload: code,
      })
    }
    async function initializeLocation() {
      const location = await getLocation()

      if (location?.status === "success") {
        setLocationInfo(location)
        dispatch({
          type: "SET_LOCATION",
          payload: location,
        })
      }

      const currencyMatches = location?.countryCode
        ? legalEntityData.filter(
            d => d?.countryCode === location?.countryCode
          )
        : []
      const currencies =
        currencyMatches?.length > 0 ? currencyMatches : defaultCurrencies
      setCurrencies(currencies)

      const savedCurrency = localStorage.getItem("currency")
      if (savedCurrency) {
        setCurrency(savedCurrency)
      } else if (location?.currency) {
        // CHECK IF THE LOCATION CURRENCY MATCHES A CURRENCY FOR THAT LOCATION
        if (currencies.find(d => d?.code === location?.currency)) {
          setCurrency(location?.currency)
        } else {
          setCurrency(currencies[0]?.code)
        }
      }
    }
    initializeLocation()
  }, [dispatch])

  const setLoading = (sku, isLoading = true) => {
    if (!state?.cart?.lineItems) return
    const lineItems = state?.cart?.lineItems?.map((item, i) => {
      if (item?.sku === sku) {
        return {
          ...item,
          isLoading: isLoading,
        }
      }
      return item
    })
    const payload = {
      ...state?.cart,
      lineItems,
    }
    dispatch({
      type: "UPDATE_CART",
      payload: payload,
    })
  }

  // CHECK ANONYMOUS CART CURRENCY; IF DIFFERENT, CREATE NEW CART
  useEffect(() => {
    const createNewCart = async () => {
      const handleGeolocationError = () => {
        localStorage.removeItem("cartId")

        // adding this to ensure cart icon in nav updates as expected. State was lingering until a page refresh
        dispatch({
          type: "UPDATE_CART",
          payload: null,
        })        
      }      
      const lineItems = state?.cart?.lineItems?.map((item, i) => ({
        sku: item?.sku,
        quantity: item?.quantity,
      }))
      const cart = await createAnonynousCart(
        state?.currency,
        countryCode,
        lineItems,
        handleGeolocationError,
        handleServerError,
      )
      if (cart?.id) {
        localStorage.setItem("cartId", cart?.id)
        updateCart(cart, false)
      }
    }
    const anonymousCartId = localStorage?.getItem("cartId")
    if (
      anonymousCartId &&
      state?.cart?.id &&
      state?.currency &&
      state?.currency !== state?.cart?.totalNet?.currencyCode
    ) {
      if (state?.cart?.lineItems && state?.cart?.lineItems?.length > 0) {
        createNewCart()
      } else {
        localStorage.removeItem("cartId")
      }
    }
  }, [state?.currency, state?.cart, countryCode])

  useEffect(() => {
    const getMyAccount = async userKey => {
      const params = new Proxy(new URLSearchParams(window.location.search), {
        get: (searchParams, prop) => searchParams.get(prop),
      })

      let accountFromUrlParam

      if (params?.account && state?.accounts) {
        accountFromUrlParam = state?.accounts?.find(
          d => params?.account === d?.id
        )
      }
      if (accountFromUrlParam && accountFromUrlParam?.id) {
        dispatch({
          type: "SET_ACCOUNT_ID",
          payload: accountFromUrlParam?.id,
        })

        dispatch({
          type: "SET_ACTIVE_ACCOUNT",
          payload: accountFromUrlParam,
        })
        sessionStorage.setItem("accountSelected", accountFromUrlParam?.id)
      } else if (sessionStorage.getItem("accountSelected") && state?.accounts) {
        const activeAccount = state.accounts.find(account => {
          return account?.id == sessionStorage.getItem("accountSelected")
        })

        if (activeAccount?.id) {
          dispatch({
            type: "SET_ACCOUNT_ID",
            payload: JSON.stringify(
              JSON.parse(sessionStorage.getItem("accountSelected"))
            ),
          })

          dispatch({
            type: "SET_ACTIVE_ACCOUNT",
            payload: activeAccount,
          })
        }
      }

      if (state?.accountId) {
        const activeAcct = state?.accounts?.find(
          d => state?.accountId === d?.id
        )
        return activeAcct
      } else {
        let accounts = await getAccounts(userKey, handleServerError)
        if (accounts?.length > 0) {
          const sortedAccounts = accounts.toSorted((a, b) => {
            if (a?.id < b?.id) return -1
            if (a?.id > b?.id) return 1
            return 0
          })

          const defaultAcct = accounts?.find(account => {
            return account?.isDefaultAccount
          })
          if (defaultAcct && defaultAcct?.id) {
            accounts = accounts.map(account => {
              return {
                ...account,
                default: account?.isDefaultAccount,
              }
            })
          }
          dispatch({
            type: "SET_ACCOUNTS",
            payload: accounts,
          })
          dispatch({
            type: "SET_ACCOUNT_ID",
            payload: defaultAcct?.id ? defaultAcct?.id : sortedAccounts[0]?.id,
          })
          dispatch({
            type: "SET_ACTIVE_ACCOUNT",
            payload: defaultAcct?.id
              ? { ...defaultAcct, default: true }
              : sortedAccounts[0],
          })

          return defaultAcct?.id ? defaultAcct : sortedAccounts[0]
        }
      }

      return null
    }

    const fetchCartData = async (
      client_id,
      client_secret,
      redirect_uri,
      code_verifier,
      path,
      setTokenRefreshing,
      tokenRefreshing,
      setProjectSightProductRemoved,
      setCartUpdatedByAPI,
      setMultiErrorMessage
    ) => {
      let data = null
      const anonymousCartId = localStorage?.getItem("cartId")
      const TIDAuth = getTIDAuth()

      setUserKey(TIDAuth)
      const id = JSON.parse(localStorage?.getItem("user-trial-data"))?.id

      // IF TID TOKEN REFRESH IS CURRENTLY HAPPENING, WE DO NOT NEED TO CONTINUE
      if (tokenRefreshing) {
        return
      }

      // IF USER IS AUTHENTICATED
      if (TIDAuth?.id) {
        // GET E-COMMERCE ACCOUNT
        let myAccount = await getMyAccount(TIDAuth?.id)

        // IF THE USER HAS AN E-COMMERCE ACCOUNT
        if (myAccount?.id) {
          setAccount(myAccount)
          
          if (myAccount?.address?.country) {
            localStorage.setItem("country", myAccount?.address?.country)
          }

          // SET CURRENCY
          if (myAccount?.currency) {
            dispatch({
              type: "SET_CURRENCY",
              payload: myAccount?.currency,
            })
            localStorage.setItem("currency", myAccount?.currency)
          }

          // IF THE USER HAS AN ANONYMOUS CART
          if (anonymousCartId) {
            let isDvwAccount = myAccount?.sourceSystems?.includes("DVW")

            // IF THE ACCOUNT IS NOT A DVW ACCOUNT
            if (!isDvwAccount) {
              // LET'S TRY TO PROVISION THE ACCOUNT
              if (
                myAccount?.role === "ACCOUNT_OWNER" ||
                myAccount?.role === "SECONDARY_ACCOUNT_OWNER"
              ) {
                setMigratingAccount(true)
                const newAccount = await provisionAccount(
                  TIDAuth?.id,
                  myAccount?.id,
                  handleServerError
                )
                setMigratingAccount(false)

                // IF ACCOUNT IS SUCCESSFULLY PROVISIONED
                if (newAccount) {
                  setAccount(newAccount)

                  isDvwAccount = true

                  // UPDATE ACCOUNT IN CONTEXT
                  dispatch({
                    type: "PATCH_ACCOUNTS",
                    payload: newAccount,
                  })
                  dispatch({
                    type: "SET_ACTIVE_ACCOUNT",
                    payload: newAccount,
                  })
                }

                // IF ACCOUNT IS NOT AN ACCOUNT OWNER
              } else {
                if (
                  !signInToCheckout({
                    account: myAccount?.id,
                    cartId: anonymousCartId,
                    referrerUrl: url,
                  })
                ) {
                  data = await getAnonymousCartData(
                    anonymousCartId,
                    handleServerError
                  )
                }
              }
            }

            if (isDvwAccount) {
              // WE KEEP ANONYMOUS CART FOR LICENSE ADMINS AND PRODUCT USERS
              if (
                myAccount?.role === "COMPANY_ADMIN" ||
                myAccount?.role === "PRODUCT_USER"
              ) {
                if (
                  !signInToCheckout({
                    account: myAccount?.id,
                    cartId: anonymousCartId,
                    referrerUrl: url,
                  })
                ) {
                  data = await getAnonymousCartData(
                    anonymousCartId,
                    handleServerError
                  )
                }
              } else {
                // REMOVE ANONYMOUS CART
                localStorage.removeItem("cartId")

                let isSubscriptionError = false
                const handleSubscriptionError = () => {
                  isSubscriptionError = true
                  setSubscriptionError(true)
                }

                let isGeolocationError = false
                const handleGeolocationError = () => {
                  isGeolocationError = true
                  setCartUpdatedByAPI(true)
                }

                let isMergeError = false
                const handleMergeError = data => {
                  console.log("Merge API response", data)
                  isMergeError = true
                  
                  const mergeWarningForRegionBlockedProduct =
                    data?.mergeWarning?.find(warning => {
                      return warning?.key === "REMOVED_SKU"
                    })
                  
                  const mergeWarningForCurrencyMismatch =
                    data?.mergeWarning?.find(warning => {
                      return warning?.key === "CURRENCY_MISMATCH"
                    })

                  const mergeWarningForThresholdExceeded = data?.mergeWarning?.find(warning => {
                    return warning?.key === "THRESHOLD_EXCEED"
                  })
                  // ERROR TO REMOVE PROJECT SIGHT FROM ROW USERS ONLY
                  if (
                    mergeWarningForRegionBlockedProduct &&
                    !mergeWarningForCurrencyMismatch?.key && !mergeWarningForThresholdExceeded?.key
                  ) {
                    setProjectSightProductRemoved({
                      removed: true,
                      products: mergeWarningForRegionBlockedProduct?.value,
                    })
                  // ERROR TO REMOVE PROJECT SIGHT FROM ROW USERS AND DISPLAY CURRENCY MISMATCH ERROR
                  } else if (
                    mergeWarningForRegionBlockedProduct &&
                    mergeWarningForCurrencyMismatch?.key && !mergeWarningForThresholdExceeded?.key
                  ) {
                    setMultiErrorMessage({
                      currencyMismatch: true,
                      products: mergeWarningForRegionBlockedProduct?.value,
                    })
                  } else if (mergeWarningForThresholdExceeded?.key && mergeWarningForRegionBlockedProduct && !mergeWarningForCurrencyMismatch?.key) {
                      setMultiErrorMessage({
                      thresholdErrorAndRegionError: true,
                      products: mergeWarningForRegionBlockedProduct?.value,
                    })
                  }
                  // ERROR TO REMOVE PROJECT SIGHT FROM ROW USERS, DISPLAY CURRENCY MISMATCH AND THRESHOLD ERRORS
                  else if ( mergeWarningForCurrencyMismatch?.key && mergeWarningForThresholdExceeded?.key && mergeWarningForRegionBlockedProduct) {
                    setMultiErrorMessage({
                      thresholdCurrencyMisMatchAndRegionBlockedErrors: true,
                      products: [mergeWarningForRegionBlockedProduct?.value]
                    })
                    // ERROR FOR CURRENCY MISMATCH
                  } else if (mergeWarningForCurrencyMismatch?.key && !mergeWarningForThresholdExceeded?.key && !mergeWarningForRegionBlockedProduct ) {
                    setMultiErrorMessage({currencyMismatchErrorOnly: true})
                    //ERROR FOR THRESHOLD AND CURRENCY MISMATCH ONLY - NEED PROPER ERROR MESSAGING
                  } else if (mergeWarningForCurrencyMismatch?.key && mergeWarningForThresholdExceeded?.key && !mergeWarningForRegionBlockedProduct) {
                    setMultiErrorMessage({currencyMismatchErrorAndThresholdError: true})
                    // ERROR FOR ONLY THRESHOLD ONLY
                  } else if (mergeWarningForThresholdExceeded?.key && !mergeWarningForCurrencyMismatch?.key && !mergeWarningForRegionBlockedProduct) {
                    setMultiErrorMessage({thresholdErrorOnly: true})
                  }
                }

                // ASSIGN ANONYMOUS CART TO ACCOUNT
                data = await assignAnonymousCart(
                  TIDAuth?.id,
                  myAccount?.id,
                  anonymousCartId,
                  handleSubscriptionError,
                  handleGeolocationError,
                  handleMergeError,
                  handleServerError,
                )

                // IF 'SIGN IN TO CHECKOUT' HAS BEEN INITIATED
                if (
                  !isSubscriptionError &&
                  !isGeolocationError &&
                  !isMergeError
                ) {
                  signInToCheckout({
                    account: myAccount?.id,
                    referrerUrl: url,
                  })
                }
              }
            }

            // HAS AN E-COMMERCE ACCOUNT
            // NO ANONYMOUS CART
          } else {
            // WITHOUT THIS, THE ADD TO CART BUTTON ON PDP STAYS DISABLED FOREVER
            if (
              myAccount?.role === "COMPANY_ADMIN" ||
              myAccount?.role === "PRODUCT_USER"
            ) {
              data = null
            } else {
              data = await getRegisteredCartData(
                TIDAuth?.id,
                myAccount?.id,
                handleServerError
              )
            }
          }

          // NO E-COMMERCE ACCOUNT
          // HAS AN ANONYMOUS CART
        } else if (anonymousCartId) {
          if (
            !signInToCheckout({
              cartId: anonymousCartId,
              referrerUrl: url,
            })
          ) {
            data = await getAnonymousCartData(
              anonymousCartId,
              handleServerError
            )
          }
        }

        // IF USER IS NOT AUTHENTICATED
      } else if (!TIDAuth?.id && id) {
        // IF TOKEN IS EXPIRED AND OLD TOKEN CREDENTIALS ARE IN LOCALSTORAGE, WE REFRESH TOKEN HERE
        refreshAuthToken(
          client_id,
          client_secret,
          redirect_uri,
          code_verifier,
          path,
          process.env.GATSBY_ENV === "staging"
            ? "https://stage.id.trimblecloud.com/oauth/token"
            : "https://id.trimble.com/oauth/token",
          setUserKey,
          setTokenRefreshing
        )
      } else if (anonymousCartId) {
        data = await getAnonymousCartData(anonymousCartId, handleServerError)
      }

      let payload = null
      if (data) {
        const lineItems = formatLineItems(
          data?.lineItems,
          state?.cart?.lineItems
        )
        payload = { openFlyout: false, ...data, lineItems }
      }

      dispatch({
        type: "UPDATE_CART",
        payload: payload,
      })
      dispatch({ type: "SET_CART_INITIALIZED", payload: true })
    }

    if (disableCartFlyout === false) {
      dispatch({ type: "SET_CART_INITIALIZED", payload: false })
      fetchCartData(
        client_id,
        client_secret,
        redirect_uri,
        code_verifier,
        path,
        setTokenRefreshing,
        tokenRefreshing,
        setProjectSightProductRemoved,
        setCartUpdatedByAPI,
        setMultiErrorMessage
      )
    }
  }, [
    isAuthenticated,
    disableCartFlyout,
    state?.accountId,
    state?.accounts,
    dispatch,
    referrerUrl,
    setMigratingAccount,
    tokenRefreshing,
  ])

  // WHEN THE CART HAS CHANGED, OPEN THE FLYOUT
  useEffect(() => {
    if (state?.cart && state?.cart?.openFlyout !== false) {
      setIsCartFlyoutOpen(true)
    }
  }, [state?.cart])

  const handleSignIn = async () => {
    const path = window?.location?.pathname
    sessionStorage.setItem("checkout", "true")
    window.open(`${queryString}${path}`, "_self")
  }

  const cartSettings = {
    cartTitle: rawFlyoutCartData?.cartTitle || "My Cart",
    subtotalLabel: rawFlyoutCartData?.subtotalLabel || "Subtotal",
    productsTitle: rawFlyoutCartData?.productsTitle || "Your items",
    productsRelatedTitle:
      rawFlyoutCartData?.productsRelatedTitle || "You may also like",
    addedToCartMessage:
      rawFlyoutCartData?.addedToCartMessage ||
      "{Product Name} was added to cart",
    removeFromCartMessage:
      rawFlyoutCartData?.removeFromCartMessage ||
      "{Product Name} was removed from the cart",
    emptyCartTitle:
      rawFlyoutCartData?.emptyCartTitle || "Your cart is currently empty.",
    emptyCartMessage:
      rawFlyoutCartData?.emptyCartMessage ||
      "There is nothing in your cart. Let's add some products.",
    errorMessageForMaxQuantity:
      rawFlyoutCartData?.errorMessageForMaxQuantity ||
      "The quantity selected exceeds the maximum allowed for this transaction. Please contact sales to complete your order.",
    errorMessageForMinQuantity:
      rawFlyoutCartData?.errorMessageForMinQuantity ||
      "Quantity must be at least 1",
    viewCartBtnLabel: rawFlyoutCartData?.viewCartBtnLabel || "View Cart",
    viewCartBtnUrl: isProjectSight
      ? null
      : (account?.id && account?.role === "PRODUCT_USER") ||
        (account?.id && account?.role === "COMPANY_ADMIN")
      ? `${checkoutBaseUrl}/carts?account=${account?.id}&cartId=${state?.cart?.id}&referrerUrl=${referrerUrl}`
      : account?.id
      ? `${checkoutBaseUrl}/carts?account=${account?.id}&referrerUrl=${referrerUrl}`
      : state?.cart?.id
      ? `${checkoutBaseUrl}/carts/${state?.cart?.id}?referrerUrl=${referrerUrl}`
      : null,
    checkoutBtnLabel: rawFlyoutCartData?.checkoutBtnLabel,
    checkoutBtnUrl:
      (account?.id && account?.role === "PRODUCT_USER") ||
      (account?.id && account?.role === "COMPANY_ADMIN")
        ? `${checkoutBaseUrl}/checkout?account=${account?.id}&cartId=${state?.cart?.id}&referrerUrl=${referrerUrl}`
        : account?.id
        ? `${checkoutBaseUrl}/checkout?account=${account?.id}&referrerUrl=${referrerUrl}`
        : userKey?.id
        ? `${checkoutBaseUrl}/checkout?cartId=${state?.cart?.id}&referrerUrl=${referrerUrl}`
        : state?.cart?.id
        ? `${checkoutBaseUrl}/account-setup?referrerUrl=${referrerUrl}`
        : null,
    continueShoppingBtnLabel:
      rawFlyoutCartData?.continueShoppingBtnLabel || "Continue Shopping",
    removeBtnLabel: rawFlyoutCartData?.removeBtnLabel || "Remove",
    signInLabel: rawFlyoutCartData?.signInLabel || "Sign In to Checkout",
    signInBtnMethod: handleSignIn,
    finePrint: rawFlyoutCartData?.finePrint?.childMarkdownRemark?.html,
    cartMenuTitle: rawFlyoutCartData?.cartMenuTitle || "Cart",
    cartMenuViewLabel: rawFlyoutCartData?.cartMenuViewLabel || "View Cart",
    currencyListTitle: rawFlyoutCartData?.currencyListTitle || "Currency",
    currencyListText: rawFlyoutCartData?.currencyListText?.text || "",
    currencyDefaultLabel: rawFlyoutCartData?.currencyDefaultLabel || "Default",
    currencySubmitLabel: rawFlyoutCartData?.currencySubmitLabel || "OK",
    currencyCancelLabel: rawFlyoutCartData?.currencyCancelLabel || "Cancel",
  }

  // UPDATE CART STATE
  const handleIsCartFlyoutOpen = isCartFlyoutOpen => {
    setIsCartFlyoutOpen(isCartFlyoutOpen)
  }

  const handleCloseSubscriptionDialog = () => {
    setSubscriptionError(false)
    setProjectSightProductRemoved(false)
    setCartUpdatedByAPI(false)
    setMultiErrorMessage(false)
    signInToCheckout({ account: account?.id, referrerUrl: url })
  }

  const handleCartChangeQuantity = async (id, sku, name, quantity) => {
    const activeToken = getTIDAuth()
    const addToCartAfterRefresh = () => {
      let refreshedKey
      if (
        userKey?.id !== JSON?.parse(localStorage.getItem("user-trial-data"))?.id
      ) {
        refreshedKey = JSON?.parse(localStorage.getItem("user-trial-data"))?.id
      }
      const url = `${productsEndpoint}/carts`

      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${refreshedKey ? refreshedKey : userKey?.id}`,
        "x-account-id": account?.id,
      }

      fetch(url, {
        method: account?.id ? "PATCH" : "PUT",
        headers: headers,
        body: JSON.stringify({
          productSku: sku,
          updateAction:
            quantity > 0 ? "UPDATE_CART_LINEITEM" : "REMOVE_CART_LINEITEM",
          lineitemId: id,
          ...(quantity > 0 && { quantity: quantity }),
        }),
      })
        .then(response => {
          return response.json().then(data => {
            if (data && !data?.errorCode) {
              updateCart(data)
              return true // SUCCESS
            }
          })
        })
        .catch(error => {
          setLoading(sku, false)
          console.error(error)
          handleServerError()
          return false // FAILURE
        })
    }

    if (!activeToken && JSON?.parse(localStorage.getItem("user-trial-data"))) {
      setTokenRefreshing(true)
      refreshAuthToken(
        client_id,
        client_secret,
        redirect_uri,
        code_verifier,
        path,
        process.env.GATSBY_ENV === "staging"
          ? "https://stage.id.trimblecloud.com/oauth/token"
          : "https://id.trimble.com/oauth/token",
        setUserKey,
        setTokenRefreshing,
        () => addToCartAfterRefresh()
      )

      setLoading(sku, true)
      return
    }
    try {
      const url =
        account?.id &&
        state?.activeAccount?.role !== "PRODUCT_USER" &&
        state?.activeAccount?.role !== "COMPANY_ADMIN"
          ? `${productsEndpoint}/carts`
          : `${productsEndpoint}/carts/${state?.cart?.id}`
      const headers =
        account?.id &&
        state?.activeAccount?.role !== "PRODUCT_USER" &&
        state?.activeAccount?.role !== "COMPANY_ADMIN"
          ? {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${userKey?.id}`,
              "x-account-id": account?.id,
            }
          : {
              "Content-Type": "application/json",
              Accept: "application/json",
            }
      setLoading(sku, true)
      const response = await fetch(url, {
        method:
          account?.id &&
          state?.activeAccount?.role !== "PRODUCT_USER" &&
          state?.activeAccount?.role !== "COMPANY_ADMIN"
            ? "PATCH"
            : "PUT",
        headers: headers,
        body: JSON.stringify({
          productSku: sku,
          updateAction:
            quantity > 0 ? "UPDATE_CART_LINEITEM" : "REMOVE_CART_LINEITEM",
          lineitemId: id,
          ...(quantity > 0 && { quantity: quantity }),
        }),
      })
      const data = await response.json()
      if (response.ok) {
        updateCart(data)
        return true // SUCCESS
      }
      throw new Error(data?.errorDescription)
    } catch (error) {
      setLoading(sku, false)
      console.error(error)
      handleServerError()
      return false // FAILURE
    }
  }

  const handleCartRemoveItem = async (id, sku) => {
    try {
      let refreshedKey
      if (
        userKey?.id !== JSON?.parse(localStorage.getItem("user-trial-data"))?.id
      ) {
        refreshedKey = JSON?.parse(localStorage.getItem("user-trial-data"))?.id
      }
      const url =
        account?.id &&
        account?.role !== "COMPANY_ADMIN" &&
        account?.role !== "PRODUCT_USER"
          ? `${productsEndpoint}/carts`
          : `${productsEndpoint}/carts/${state?.cart?.id}`
      const headers =
        account?.id &&
        account?.role !== "COMPANY_ADMIN" &&
        account?.role !== "PRODUCT_USER"
          ? {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${
                refreshedKey ? refreshedKey : userKey?.id
              }`,
              "x-account-id": account?.id,
            }
          : {
              "Content-Type": "application/json",
              Accept: "application/json",
            }
      setLoading(sku, true)
      const response = await fetch(url, {
        method:
          account?.id &&
          account?.role !== "COMPANY_ADMIN" &&
          account?.role !== "PRODUCT_USER"
            ? "PATCH"
            : "PUT",
        headers: headers,
        body: JSON.stringify({
          productSku: sku,
          updateAction: "REMOVE_CART_LINEITEM",
          lineitemId: id,
        }),
      })
      const data = await response.json()
      if (response.ok) {
        if(state?.cart?.lineItems?.length > 0){
          const itemRemoved = state?.cart?.lineItems?.filter((item) => item?.sku === sku)?.[0];
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ ecommerce: null });
          window?.dataLayer?.push({
            'event': 'remove_from_cart',
            'ecommerce': {
              'items': [
                {
                  'item_id': itemRemoved?.id,
                  'item_name': itemRemoved?.name,
                  // 'item_brand': '<product brand>',
                  'item_category': 'Software',
                  // 'item_category2': '<level 2 category>',
                  // 'item_category3': '<level 3 category>',
                  // 'item_category4': '<level 4 category>',
                  // 'item_category5': '<level 5 category>',
                  // 'item_variant': '<product variant>',
                  'affiliation': 'Trimble.com',
                  'price': itemRemoved?.totalPrice?.amount,
                  'currency': itemRemoved?.totalPrice?.currencyCode,
                  'quantity': itemRemoved?.quantity,
                  // 'coupon': '<product coupon code>',
                  // 'rating': '<product rating>',
                  // 'rating_quantity': '<product rating quantity>',
                  'sku': sku,
                  'logged_in': localStorage?.getItem("user_info") !== null ? true : false,
                  'trimble_id': localStorage && localStorage?.getItem("user_info") ? JSON.parse(localStorage?.getItem("user_info")).tid : null,
                }
              ]
            }
          });
        }
        updateCart(data)
        return true // SUCCESS
      }
      throw new Error(data?.errorDescription)
    } catch (error) {
      setLoading(sku, false)
      console.error(error)
      handleServerError()
      return false // FAILURE
    }
  }

  const defaultCurrency = currencies.find(
    d => d?.code === locationInfo?.currency
  )
    ? locationInfo?.currency
    : currencies[0]?.code

  const flyoutData = {
    cartSettings,
    handleCartChangeQuantity,
    handleCartRemoveItem,
    isCartFlyoutOpen,
    setIsCartFlyoutOpen,
    handleIsCartFlyoutOpen,
    isCurrencyLocked: typeof account?.currency === "string",
    currencies,
    currency: state?.currency,
    defaultCurrency: defaultCurrency,
    setCurrency: handleCurrencyChange,
    handleCloseSubscriptionDialog,
  }
  return flyoutData
}

export default useFlyoutCart
